import { Drawer, Select, Switch, InputNumber, Typography, Divider, Tag, Input } from 'antd';
import { LockOutlined, TeamOutlined, GlobalOutlined } from '@ant-design/icons';
import { useDashboard } from './useDashboard';
import { usePlayer } from './usePlayerContext';
import { useAuth } from '../../hooks/useAuth';
import type { PlaybackDock } from './playerCtx';
import type { DashboardVisibility } from './widgets/types';

interface DashboardSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

const ROW: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 0',
};

const VISIBILITY_OPTIONS: { value: DashboardVisibility; label: string; icon: React.ReactNode; description: string }[] = [
  { value: 'private', label: 'Private', icon: <LockOutlined />, description: 'Only you can see this dashboard' },
  { value: 'team', label: 'Team', icon: <TeamOutlined />, description: 'Visible to all users in your account' },
  { value: 'template', label: 'Template', icon: <GlobalOutlined />, description: 'Visible to all dashboard users (read-only, cloneable)' },
];

export const DashboardSettingsDrawer = ({ open, onClose }: DashboardSettingsDrawerProps) => {
  const { dashboard, draftTitle, draftDescription, draftVisibility, draftSettings, updateSettings, updateVisibility, updateTitle, updateDescription } = useDashboard();
  const { authState } = useAuth();
  const player = usePlayer();

  const isOwner = dashboard?.owner_user_id === authState.user?.id;
  const isRoot = authState.user?.role === 'root';
  const visibilityOptions = isRoot
    ? VISIBILITY_OPTIONS
    : VISIBILITY_OPTIONS.filter((o) => o.value !== 'template');

  return (
    <Drawer title="Dashboard Settings" open={open} onClose={onClose} size={340}>
      <Typography.Text strong>Title</Typography.Text>
      <div style={{ padding: '6px 0' }}>
        {isOwner ? (
          <Input
            value={draftTitle}
            onChange={(e) => updateTitle(e.target.value)}
            placeholder="Dashboard title"
            maxLength={100}
          />
        ) : (
          <Typography.Text>{draftTitle}</Typography.Text>
        )}
      </div>

      <div style={{ marginTop: 8 }}>
        <Typography.Text strong>Description</Typography.Text>
        <div style={{ padding: '6px 0' }}>
          {isOwner ? (
            <Input.TextArea
              value={draftDescription}
              onChange={(e) => updateDescription(e.target.value)}
              placeholder="What is this dashboard for?"
              rows={2}
              maxLength={500}
            />
          ) : (
            <Typography.Text type="secondary">{draftDescription || '—'}</Typography.Text>
          )}
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <Typography.Text strong>Visibility</Typography.Text>
      {isOwner ? (
        <div style={{ padding: '6px 0' }}>
          <Select<DashboardVisibility>
            value={draftVisibility}
            onChange={updateVisibility}
            style={{ width: '100%' }}
            options={visibilityOptions.map((opt) => ({
              value: opt.value,
              label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {opt.icon} {opt.label}
                </span>
              ),
            }))}
          />
          <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
            {VISIBILITY_OPTIONS.find((o) => o.value === draftVisibility)?.description}
          </Typography.Text>
        </div>
      ) : (
        <div style={{ padding: '6px 0' }}>
          <Tag icon={VISIBILITY_OPTIONS.find((o) => o.value === draftVisibility)?.icon}>
            {VISIBILITY_OPTIONS.find((o) => o.value === draftVisibility)?.label}
          </Tag>
          <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
            Only the dashboard owner can change visibility.
          </Typography.Text>
        </div>
      )}

      <Divider style={{ margin: '12px 0' }} />

      <Typography.Text strong>Timeline</Typography.Text>
      <div style={ROW}>
        <span>Compact mode</span>
        <Switch
          checked={draftSettings.timelineCompact ?? false}
          onChange={(v) => updateSettings({ timelineCompact: v })}
        />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <Typography.Text strong>Player</Typography.Text>
      <div style={ROW}>
        <span>Compact mode</span>
        <Switch
          checked={draftSettings.playerCompact ?? false}
          onChange={(v) => updateSettings({ playerCompact: v })}
        />
      </div>
      <div style={ROW}>
        <span>Speed</span>
        <Select<number>
          value={draftSettings.playbackSpeed ?? 1}
          onChange={(v) => {
            updateSettings({ playbackSpeed: v });
            player.setPlaybackSpeed(v);
          }}
          size="small"
          style={{ width: 90 }}
          options={[
            { value: 1, label: '1 fps' },
            { value: 2, label: '2 fps' },
            { value: 5, label: '5 fps' },
          ]}
        />
      </div>
      <div style={ROW}>
        <span>Loop</span>
        <Switch
          checked={draftSettings.autoLoop ?? false}
          onChange={(v) => {
            updateSettings({ autoLoop: v });
            player.setAutoLoop(v);
          }}
        />
      </div>
      <div style={ROW}>
        <span>Dock position</span>
        <Select<PlaybackDock>
          value={draftSettings.playbackDock ?? 'now'}
          onChange={(v) => {
            updateSettings({ playbackDock: v });
            player.setPlaybackDock(v);
          }}
          size="small"
          style={{ width: 110 }}
          options={[
            { value: 'first' as const, label: 'First' },
            { value: 'current' as const, label: 'Current' },
            { value: 'now' as const, label: 'Now' },
            { value: 'last' as const, label: 'Last' },
          ]}
        />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <Typography.Text strong>Live Mode</Typography.Text>
      <div style={ROW}>
        <span>Past window (min)</span>
        <InputNumber
          value={draftSettings.liveStartMinutes ?? -5}
          onChange={(v) => {
            if (v == null) return;
            updateSettings({ liveStartMinutes: v });
            player.setLiveStartMinutes(v);
          }}
          size="small"
          style={{ width: 80 }}
          max={0}
        />
      </div>
      <div style={ROW}>
        <span>Nowcast tiles</span>
        <Switch
          checked={(draftSettings.liveEndMinutesForRadar ?? 30) !== 0}
          onChange={(checked) => {
            const v = checked ? 30 : 0;
            updateSettings({ liveEndMinutesForRadar: v });
            player.setLiveEndMinutesForRadar(v);
          }}
        />
      </div>
    </Drawer>
  );
};
